import React, { lazy, Suspense } from "react";
import { routes } from "./routes";

import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Helmet, HelmetProvider } from "react-helmet-async";
import LoadingSpinner from "./components/LoadingSpinner";
import PageSkeleton from "./components/PageSkeleton";
import "./App.css";

import Motibot from "./utils/Motibot";

import heroImageSmall from "./assets/images/optimized/hero-image-400.webp";
import heroImageMedium from "./assets/images/optimized/hero-image-800.webp";
import heroImageLarge from "./assets/images/optimized/hero-image-1200.webp";

const NotFound = lazy(() => import("./pages/NotFound"));

const stripePromise = loadStripe(
  "pk_test_51RjEkLB4Kum4fbt3jbfUEPwh2VTS05DaZ7g9ut63UuaookMjSVFUHd9C5kZwBZi5UWWjUiearT8xsNrZGBlC0wGD0040Pf2X1V"
);

// Default SEO configuration
// Default SEO configuration
const defaultSEO = {
  title:
    "Last-Mile Delivery & Trucking / Bulk Hauling | Moti Transport Services",
  description:
    "Moti Transport Services provides fast last-mile delivery in Edmonton plus regional trucking and bulk hauling across Alberta and Western Canada. Furniture & appliance delivery, B2B logistics, Super-B hopper hauling, frac sand and oilfield support.",
  keywords:
    "Edmonton delivery service, trucking company Alberta, bulk hauling, Super B hopper, frac sand transport, oilfield trucking, last-mile logistics, B2B delivery, regional trucking Western Canada",
  image: "https://www.motitransport.com/assets/images/hero-image.webp",
  url: "https://www.motitransport.com",
  twitterHandle: "@motitransport",
};

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop /> {/* Add this line */}
        <div className="App">
          {/* Default SEO Meta Tags */}
          <Helmet>
            <html lang="en" />
            <title>{defaultSEO.title}</title>
            <meta name="description" content={defaultSEO.description} />
            <meta name="keywords" content={defaultSEO.keywords} />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={defaultSEO.title} />
            <meta property="og:description" content={defaultSEO.description} />
            <meta property="og:image" content={defaultSEO.image} />
            <meta property="og:url" content={defaultSEO.url} />
            <meta property="og:site_name" content="Moti Transport Services" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={defaultSEO.title} />
            <meta name="twitter:description" content={defaultSEO.description} />
            <meta name="twitter:image" content={defaultSEO.image} />
            <meta name="twitter:site" content={defaultSEO.twitterHandle} />
            <meta name="twitter:creator" content={defaultSEO.twitterHandle} />
            <link rel="canonical" href={defaultSEO.url} />
            {/* Critical performance optimizations */}
            <style>{`
              .hero-section {
                padding: 6rem 1.25rem;
                background-color: #2d5d7c;
                text-align: left;
                position: relative;
                overflow: hidden;
              }

              .hero-content {
                  position: relative;
                  z-index: 2;
                  max-width: 800px;
                  margin-left: 0;
                  margin-right: auto;
                }

              .hero-content h1 {
                font-size: 2.5rem;
                font-weight: 700;
                color: white;
                margin-bottom: 1.25rem;
              }

              .hero-content p {
                font-size: 1.125rem;
                color: white;
                margin-bottom: 2.5rem;
              }

              .primary-cta {
                background: #ff6f61;
                color: white;
                padding: 0.75rem 1.5rem;
                border: none;
                font-size: 1.125rem;
                cursor: pointer;
                border-radius: 5px;
                transition: background 0.3s ease;
                text-decoration: none;
                display: inline-block;
              }

              .primary-cta:hover {
                background: #e65a50;
              }
            `}</style>

            <link rel="preconnect" href="https://js.stripe.com" />

            <link rel="preload" as="image" href={heroImageLarge} />

            <script
              async
              src="https://www.googletagmanager.com/gtag/js?id=G-M7HV77RQG4"
            ></script>
            <script>
              {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-M7HV77RQG4');
              `}
            </script>

            <link
              rel="preload"
              href="https://fonts.googleapis.com/css2?family=Montserrat&display=swap"
              as="style"
            />
            <link
              rel="stylesheet"
              href="https://fonts.googleapis.com/css2?family=Montserrat&display=swap"
            />
            <link
              rel="preload"
              href="/fonts/montserrat-v25-latin-regular.woff2"
              as="font"
              type="font/woff2"
              crossOrigin="anonymous"
            />

            {/* Critical CSS */}
            <style>{`
              .hero-section, .hero-content, .navbar {
                opacity: 1 !important;
                visibility: visible !important;
              }
            `}</style>
            <meta name="robots" content="index, follow" />
          </Helmet>

          {/* Top Navigation Bar */}
          <nav className="navbar">
            <div className="logo">Moti</div>
            <ul className="nav-links">
              <li className="home-btn">
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/services">Services</Link>
              </li>
              <li>
                <Link to="/trucking">Trucking &amp; Bulk Hauling</Link>
              </li>
              <li>
                <Link to="/contact">Contact</Link>
              </li>

              <li className="track-btn">
                <Link to="/track-delivery">Track My Delivery</Link>
              </li>

              <li className="login-btn">
                <a
                  href="https://moti-client.vercel.app/login"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Client Login
                </a>
              </li>
            </ul>
          </nav>

          {/* Define Routes */}
          <Elements stripe={stripePromise}>
            <Routes>
              {/* Home Page Route */}
              <Route
                path="/"
                element={
                  <>
                    <Helmet>
                      <title>
                        Last-Mile Delivery & Regional Trucking in Edmonton |
                        Moti Transport Services
                      </title>
                      <meta
                        name="description"
                        content="Moti Transport Services offers reliable last-mile delivery in Edmonton plus regional trucking and bulk hauling across Alberta and Western Canada. Schedule city deliveries or request bulk hauling today."
                      />
                      <script type="application/ld+json">
                        {JSON.stringify({
                          "@context": "https://schema.org",
                          "@type": "LocalBusiness",
                          name: "Moti Transport Services",
                          image: defaultSEO.image,
                          description: defaultSEO.description,
                          address: {
                            "@type": "PostalAddress",
                            streetAddress: "16745 111 Ave NW",
                            addressLocality: "Edmonton",
                            addressRegion: "AB",
                            addressCountry: "CA",
                          },
                          telephone: "+1-780-901-3330",
                          url: defaultSEO.url,
                          areaServed: {
                            "@type": "AdministrativeArea",
                            name: "Alberta and Western Canada",
                          },
                          openingHoursSpecification: {
                            "@type": "OpeningHoursSpecification",
                            dayOfWeek: [
                              "Monday",
                              "Tuesday",
                              "Wednesday",
                              "Thursday",
                              "Friday",
                            ],
                            opens: "08:00",
                            closes: "18:00",
                          },
                          priceRange: "$$",
                        })}
                      </script>
                    </Helmet>

                    {/* Hero Section */}

                    <header className="hero-section">
                      <picture>
                        <source
                          srcSet={heroImageLarge}
                          media="(min-width: 1024px)"
                        />
                        <source
                          srcSet={heroImageMedium}
                          media="(min-width: 768px)"
                        />
                        <img
                          src={heroImageSmall}
                          alt="Moti Transport - Delivery & Trucking Services in Edmonton"
                          className="hero-image"
                          width="1200"
                          height="675"
                          loading="eager"
                          fetchpriority="high"
                          decoding="async"
                        />
                      </picture>

                      <div className="hero-content">
                        <h1>Reliable Last-Mile Delivery for Your Business</h1>
                        <p>
                          Fast, secure, and efficient city delivery for
                          furniture, appliances, and B2B/B2C needs.
                        </p>
                        <Link to="/booking" className="primary-cta">
                          Book Your Delivery Now!
                        </Link>
                      </div>
                    </header>

                    {/* Services Overview */}
                    <section className="services-overview">
                      <h2>Our Services</h2>
                      <div className="services-list">
                        <div className="service-card">
                          <h3>Furniture Delivery</h3>
                          <p>
                            Efficient and professional handling of all furniture
                            deliveries.
                          </p>
                        </div>
                        <div className="service-card">
                          <h3>Appliance Delivery</h3>
                          <p>
                            Secure and reliable delivery for appliances of all
                            sizes.
                          </p>
                        </div>
                        <div className="service-card">
                          <h3>B2B/B2C Solutions</h3>
                          <p>
                            Customized solutions for business-to-business and
                            business-to-customer deliveries.
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* Why Choose Moti? */}
                    <section className="why-choose">
                      <h2>Why Choose Moti?</h2>
                      <ul>
                        <li>🚚 Fast & Reliable Deliveries</li>
                        <li>📍 Real-time Tracking</li>
                        <li>📦 Professional Handling of Large Items</li>
                      </ul>
                    </section>

                    <Motibot />

                    <section className="seo-content">
                      <div className="seo-container">
                        <h2>Reliable Delivery & Logistics in Edmonton</h2>

                        <p className="seo-intro">
                          <strong>Moti</strong> offers trusted delivery services
                          for a wide range of items — including furniture,
                          appliances, retail products, and more. Whether you're
                          a business or an individual, our easy-to-use online
                          booking system lets you schedule deliveries in
                          minutes, with full visibility through real-time
                          tracking and instant delivery confirmation.
                        </p>

                        <div className="seo-features">
                          <div className="seo-feature-card">
                            <div className="seo-feature-icon">🚛</div>
                            <h3>Delivery Experts</h3>
                            <p>
                              We deliver furniture, appliances, and a variety of
                              oversized items with care and precision. From
                              single-item drop-offs to larger residential or
                              commercial moves, Moti ensures smooth, secure
                              service every time.
                            </p>
                          </div>

                          <div className="seo-feature-card">
                            <div className="seo-feature-icon">📱</div>
                            <h3>Real-Time Shipment Tracking</h3>
                            <p>
                              Monitor your <strong>Edmonton delivery</strong> in
                              real-time with our advanced GPS tracking system.
                              Receive text alerts for ETAs and get digital proof
                              of delivery with photos and signatures.
                            </p>
                          </div>

                          <div className="seo-feature-card">
                            <div className="seo-feature-icon">🏢</div>
                            <h3>B2B Logistics Solutions</h3>
                            <p>
                              Streamline your business operations with our
                              reliable <strong>B2B delivery service</strong>. We
                              offer scheduled routes, inventory management
                              integration, and dedicated account managers for
                              seamless logistics.
                            </p>
                          </div>
                        </div>

                        <Link to="/booking" className="seo-cta">
                          Schedule Your Delivery Today
                        </Link>

                        <div className="service-areas">
                          <h3>Serving All of Edmonton & Surrounding Areas</h3>
                          <p>
                            We provide reliable{" "}
                            <strong>
                              delivery services throughout Edmonton
                            </strong>
                            , including St. Albert, Sherwood Park, Beaumont,
                            Leduc, and surrounding communities. Our fleet is
                            equipped to handle urban and suburban deliveries
                            with equal efficiency.
                          </p>
                        </div>
                      </div>
                    </section>
                    {/* Testimonials Section */}
                    <section className="testimonials">
                      <h2>What Our Clients Say</h2>
                      <div className="testimonial-cards">
                        <div className="testimonial-card">
                          <p>⭐⭐⭐⭐⭐</p>
                          <p>
                            "Moti made our furniture deliveries effortless!
                            Their tracking system is a game-changer."
                          </p>
                          <p>- Jane D., Home Furnishings Co.</p>
                        </div>
                        <div className="testimonial-card">
                          <p>⭐⭐⭐⭐⭐</p>
                          <p>
                            "Fast, professional, and reliable. We trust Moti for
                            all our B2B shipments."
                          </p>
                          <p>- Mark R., Logistics Manager</p>
                        </div>
                      </div>
                    </section>
                  </>
                }
              />

              {/* Booking Page Route */}
              {routes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  exact={route.exact}
                  element={
                    <Suspense
                      fallback={
                        route.loadingType === "page" ? (
                          <PageSkeleton />
                        ) : (
                          <LoadingSpinner />
                        )
                      }
                    >
                      <route.component />
                    </Suspense>
                  }
                />
              ))}
              {/* 404 route */}
              <Route
                path="*"
                element={
                  <>
                    <Helmet>
                      <title>Page Not Found | Moti Transport Services</title>
                      <meta name="robots" content="noindex, follow" />
                    </Helmet>
                    <Suspense fallback={<PageSkeleton />}>
                      <NotFound />
                    </Suspense>
                  </>
                }
              />
            </Routes>
          </Elements>

          {/* Footer */}
          {/* Footer */}
          <footer className="footer">
            <div className="footer-container">
              <div className="footer-column">
                <h4>Quick Links</h4>
                <ul>
                  <li>
                    <Link to="/">Home</Link>
                  </li>
                  <li>
                    <Link to="/services">Services</Link>
                  </li>
                  <li>
                    <Link to="/trucking">Trucking &amp; Bulk Hauling</Link>
                  </li>
                  <li>
                    <Link to="/about">About Us</Link>
                  </li>
                  <li>
                    <Link to="/contact">Contact</Link>
                  </li>
                  <li>
                    <Link to="/track-delivery">Track Delivery</Link>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <h4>Services</h4>
                <ul>
                  <li>
                    <Link to="/booking">Furniture Delivery</Link>
                  </li>
                  <li>
                    <Link to="/booking">Appliance Delivery</Link>
                  </li>
                  <li>
                    <Link to="/booking">B2B Solutions</Link>
                  </li>
                  <li>
                    <Link to="/booking">Retail Deliveries</Link>
                  </li>
                  <li>
                    <Link to="/booking">Special Items</Link>
                  </li>
                  <li>
                    <Link to="/trucking">Trucking &amp; Bulk Hauling</Link>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <h4>Legal</h4>
                <ul>
                  <li>
                    <Link to="/privacy-policy">Privacy Policy</Link>
                  </li>
                  <li>
                    <Link to="/terms-of-service">Terms of Service</Link>
                  </li>
                  <li>
                    <Link to="/faq">FAQ</Link>
                  </li>
                  <li>
                    <Link to="/app-privacy-policy">App Privacy Policy</Link>
                  </li>
                  <li>
                    <Link to="/data-deletion-request">Data Deletion</Link>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-contact">
                  <p>
                    <span className="footer-contact-icon">📍</span>
                    16745 111 Ave NW, Edmonton, AB
                  </p>
                  <p>
                    <span className="footer-contact-icon">📞</span>
                    <a href="tel:+17809013330">
                      <span>780</span>-<span>901</span>-<span>3330</span>
                    </a>
                  </p>
                  <p>
                    <span className="footer-contact-icon">📧</span>
                    <a
                      href={`mailto:info@motitransport.com`}
                      aria-label="Email Moti Support"
                    >
                      <span>info</span>@<span>motitransport.com</span>
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="footer-bottom">
              <p>
                © {new Date().getFullYear()} Moti Transport Services. All Rights
                Reserved.
              </p>
            </div>
          </footer>
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
